import { useDropzone } from "react-dropzone";
// @mui
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
// type
import { UploadProps } from "./type";
//
import Image from "../Image";
import BlockContent from "./BlockContent";
import { BlockContentV2 } from "./BlockContentV2";
import RejectionFiles from "./RejectionFiles";

// ----------------------------------------------------------------------

export const DropZoneStyle = styled("div")(({ theme }) => ({
  outline: "none",
  overflow: "hidden",
  position: "relative",
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create("padding"),
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  "&:hover": { opacity: 0.72, cursor: "pointer" },
}));

// ----------------------------------------------------------------------

export default function UploadSingleFile({
  error = false,
  file,
  helperText,
  sx,
  fileName,
  accept,
  isBlockContentV2Enabled = false,
  contentFooter,
  ...other
}: UploadProps) {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    accept,
    multiple: false,
    ...other,
  });

  const fileMessage = `only_one_document_is_allowed_to_upload`;

  let acceptedFiles = "";
  if (accept && accept.length) {
    acceptedFiles = typeof accept === "object" ? accept?.join(", ") : accept;
  }

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: "error.main",
            borderColor: "error.light",
            bgcolor: "error.lighter",
          }),
          ...(file && {
            padding: "12% 0",
          }),
        }}
      >
        <input {...getInputProps()} />
        {renderBlockContent({
          acceptedFiles,
          fileName,
          fileMessage,
          isBlockContentV2Enabled,
          contentFooter,
        })}

        {file && (
          <Image
            alt="file preview"
            src={typeof file === "string" ? file : file.preview}
            sx={{
              top: 8,
              left: 8,
              borderRadius: 1,
              position: "absolute",
              width: "calc(100% - 16px)",
              height: "calc(100% - 16px)",
            }}
          />
        )}
      </DropZoneStyle>

      {fileRejections.length > 0 && (
        <RejectionFiles fileRejections={fileRejections} />
      )}

      {helperText && helperText}
    </Box>
  );
}

export const renderBlockContent = ({
  acceptedFiles,
  fileName,
  fileMessage,
  isBlockContentV2Enabled,
  contentFooter,
}: {
  acceptedFiles: string;
  fileName?: string;
  fileMessage: string;
  isBlockContentV2Enabled: boolean;
  contentFooter?: string;
}) => {
  if (isBlockContentV2Enabled) {
    return (
      <BlockContentV2
        acceptedFiles={acceptedFiles}
        contentFooter={contentFooter}
      />
    );
  }
  return <BlockContent fileName={fileName} fileMessage={fileMessage} />;
};
